import { useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import type { EnvironmentSummary } from "@starsach/shared-types";
import { useCustomers } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { actsAsCustomer, focusEnvironment, type Module, modulePath } from "../lib/modules";
import { useUiStore } from "../store/ui";
import { Card, ErrorState, LoadingState, PageHeader } from "./ui";

/** Every environment the signed-in person can see - the same list Customers shows, cached by TanStack Query. */
export function useVisibleEnvironments() {
  const window = useUiStore((s) => s.window);
  return useCustomers(window);
}

/** Friendly names for environment ids the API hands back bare (a chain, a promotion's source). */
export function useEnvironmentName(): (environmentId: string) => string {
  const envs = useVisibleEnvironments();
  const names = new Map((envs.data?.customers ?? []).map((e) => [e.environmentId, e.friendlyName]));
  return (id) => names.get(id) ?? id;
}

/** Remembers the environment a module is showing, so the nav opens the next module on the same one. */
export function useRememberEnvironment(environmentId: string | undefined) {
  const setLast = useUiStore((s) => s.setLastEnvironment);
  useEffect(() => {
    if (environmentId) setLast(environmentId);
  }, [environmentId, setLast]);
}

/**
 * The environment axis, in the page header rather than the nav (section
 * 8.9): a select of what the person can see, and for staff on a module
 * with a cross-customer page, "All customers". Changing it navigates -
 * the environment lives in the path, so the address stays shareable.
 */
export function EnvironmentPicker({ module, environmentId, section }: { module: Module; environmentId: string | undefined; section?: string }) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const envs = useVisibleEnvironments();
  const allowAll = module.crossCustomer && !actsAsCustomer(me);
  const options = envs.data?.customers ?? [];
  const current = options.find((e) => e.environmentId === environmentId);
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-neutral-600 dark:text-neutral-400">Environment</span>
      <select
        aria-label="Environment"
        value={environmentId ?? ""}
        onChange={(e) => navigate(e.target.value ? modulePath(module, e.target.value, section) : module.path)}
        className="min-h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-medium text-ink dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
      >
        {allowAll && <option value="">All customers</option>}
        {!allowAll && !current && <option value="">Choose…</option>}
        {options.map((e) => (
          <option key={e.environmentId} value={e.environmentId}>
            {e.friendlyName}
          </option>
        ))}
        {environmentId && !current && envs.data && <option value={environmentId}>{environmentId}</option>}
      </select>
    </label>
  );
}

/**
 * A module address with no environment: go to the module's focus
 * environment (section 13.1), else the one the person last looked at if
 * they can still see it, else their only one, else ask.
 */
export function EnvironmentGate({ module, section }: { module: Module; section?: string }) {
  const { preferences } = useAuth();
  const last = useUiStore((s) => s.lastEnvironmentId);
  const envs = useVisibleEnvironments();
  if (envs.isPending) return <LoadingState label="Loading environments" />;
  if (envs.isError) return <ErrorState error={envs.error} onRetry={() => void envs.refetch()} />;
  const list = envs.data.customers;
  const wanted = focusEnvironment(module, preferences, new Set(list.map((e) => e.environmentId)), last);
  const target = list.find((e) => e.environmentId === wanted) ?? (list.length === 1 ? list[0] : undefined);
  if (target) return <Navigate to={modulePath(module, target.environmentId, section)} replace />;
  return <EnvironmentChooser module={module} section={section} environments={list} />;
}

export function EnvironmentChooser({ module, section, environments }: { module: Module; section?: string; environments: EnvironmentSummary[] }) {
  return (
    <>
      <PageHeader title={module.label} subtitle="Which environment?" />
      <Card>
        {environments.length === 0 ? (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">No environment is visible to you yet.</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {environments.map((e) => (
              <li key={e.environmentId}>
                <Link to={modulePath(module, e.environmentId, section)} className="block rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-ink hover:border-accent hover:bg-accent-soft dark:border-neutral-800 dark:text-white dark:hover:bg-neutral-800">
                  {e.friendlyName}
                  <span className="mt-0.5 block font-mono text-xs font-normal text-neutral-500">{e.awsAccountNumber}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
